import React from "react";
import { Check, Clock, X } from "lucide-react";
import { ICONS } from "../../utils/icons";
import { DESIGN_TOKENS } from "../../constants/designTokens";
import { getDepartmentClassLabel } from "../../utils/studentDisplay";
import { getAttendanceMobileDailyStatusButtonClass } from "./attendanceMobileStyles";
import { getNextAttendanceStatus } from "./attendanceViewDataUtils";

export default function AttendanceMobileDailyList({
  dailyAttendance,
  filteredStudents,
  handleSelectRow,
  handleStatusChange,
  selectedIds,
  targetGroup,
  selectedDepartment,
  handleMarkDragStart,
  handleSelectDragStart,
  handleDragOver,
  isDragging,
  dragStatus,
}) {
  const brand = DESIGN_TOKENS.brand;
  const shape = DESIGN_TOKENS.shape;
  const lastTouchTimeRef = React.useRef(0); // [NEW] 터치 후 발생하는 고스트 클릭 방지용

  return (
    <div className="px-2 pt-1 pb-40">
      <div className="grid grid-cols-2 gap-1.5">
        {filteredStudents.map((s) => {
          const status = dailyAttendance[s.id] || "none";
          const isSelected = selectedIds.includes(s.id);
          const showDepartment = targetGroup !== "students" || selectedDepartment === "All";
          const affiliationLabel = getDepartmentClassLabel(s, { showDepartment });

          return (
            <div
              key={s.id}
              data-attendance-row-id={s.id}
              onMouseDown={(e) => {
                // [FIXED] 터치 디바이스에서 발생하는 시뮬레이트된 마우스 이벤트 무시 (500ms 가드)
                if (Date.now() - lastTouchTimeRef.current < 500) return;
                if (!e.touches) handleSelectDragStart?.(s.id, e);
              }}
              onTouchStart={(e) => {
                lastTouchTimeRef.current = Date.now();
                handleSelectDragStart?.(s.id, e);
                if (window.navigator.vibrate) window.navigator.vibrate(8);
              }}
              onMouseEnter={() => handleDragOver?.(s.id)}
              className={`group relative bg-white p-2.5 border transition-all cursor-pointer active:scale-[0.98] ${shape.rounded2xlClass} 
                ${isSelected 
                  ? `${brand.primaryBorderSolidClass} ring-2 ${brand.primaryRingSoftClass} ${brand.primaryBgSoftClass} shadow-md z-10 scale-[1.02]` 
                  : "border-gray-100 hover:border-gray-200 shadow-sm"} 
                ${s.faithState === "장기결석" && !isSelected ? "opacity-50 grayscale-[0.5]" : ""} 
                selection-item`}
            >
              <div className="flex items-center justify-between gap-1 min-w-0">
                <div className="min-w-0 flex items-center gap-2">
                  <div className="relative flex-shrink-0">
                    <div className={`w-5 h-5 border-2 flex items-center justify-center rounded-full transition-all duration-300 ${isSelected ? `${brand.primaryBgClass} ${brand.primaryBorderSolidClass} scale-110 shadow-sm` : "border-gray-300 bg-white"}`}>
                      {isSelected && <Check size={12} strokeWidth={4} className="text-white" />}
                    </div>
                    {s.faithState === "장기결석" && (
                      <div className="absolute -top-1 -right-1 bg-gray-500 text-white text-[6px] font-black w-3 h-3 flex items-center justify-center rounded-full border border-white z-10">L</div>
                    )}
                  </div>

                  <div className="min-w-0 overflow-hidden">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <div className="font-black text-gray-800 text-[14px] truncate leading-tight">{s.name}</div>
                      {s.faithState && s.faithState !== "기존신자" && s.faithState !== "장기결석" && (
                        <span className={`text-[8px] px-1.5 py-0.5 rounded font-black text-white leading-none flex-shrink-0 ${s.faithState === "초신자" ? "bg-amber-400" : "bg-gray-400"}`}>
                          {s.faithState === "초신자" ? "NEW" : s.faithState}
                        </span>
                      )}
                    </div>
                    {affiliationLabel && (
                      <div className="text-[10px] text-gray-500 font-bold truncate leading-none opacity-80">
                        {affiliationLabel}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Dot Display Only - [RE-REFINED] Restore circle badge style */}
                <div 
                  className="flex-shrink-0 -mr-1 p-2"
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <div className={`w-7 h-7 rounded-2xl flex items-center justify-center shadow-lg transition-transform ${isSelected ? 'scale-105' : ''}
                    ${status === "present" ? "bg-green-500" : 
                      status === "late" ? "bg-amber-400" : 
                      status === "absent" ? "bg-red-500" : 
                      "bg-gray-50 border border-gray-100 opacity-60 shadow-inner"}`}
                  >
                    {status !== "none" ? (
                      <div className="w-2.5 h-2.5 rounded-full bg-white shadow-sm" />
                    ) : (
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
